import React, { useMemo, useState } from "react"
import PropTypes from "prop-types"
import keyBy from "lodash.keyby"
import { Button } from "antd"
import { useOutletContext } from "react-router-dom"

import { getConfig } from "@components/Config"
import { getUsDateFromIsoDate } from "@components/Date"
import {
  IndexTransactionsOutputShape,
  transferTransactionsOperation,
} from "@api/services/transactions"
import {
  IndexProjectBatchesOutputShape,
  IndexBatchFilesOutputShape,
  transferInvestorFilesOperation,
} from "@api/services/backstage"
import {
  updateInvestmentOperation,
  transferInvestmentsOperation,
} from "@api/services/investments"
import { useAppContext } from "@components/AppContext"

import getPayloads from "./helpers/getPayloads"
import useTransferItems from "./helpers/useTransferItems"
import useTargetBatches from "./helpers/useTargetBatches"
import { InvestmentShape } from "../shapes"

const LABEL_SUBMIT = "Start Transfer"

const RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS =
  getConfig("RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS")


const InvestmentsTransferFooter = ({
  table,
  sourceFiles,
  sourceBatches,
  sourceProjectId,
  sourceTransactions,
  sourceInvestments,
  targetProjectId = undefined,
  targetBatches,
  targetInvestments,
  customAmounts,
  customTargets,
  setTargetBatches,
  onAfterComplete,
}) => {
  const [ isLoading, setIsLoading ] = useState(false)

  const { getProjectName } = useOutletContext()
  const { request, identity } = useAppContext()

  const isTransferInvestmentsSubmitAvailable =
    RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS.length === 0 ||
    RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS.includes(identity.email)

  const customAmountsMap = useMemo(() =>
    keyBy(customAmounts, "investmentId")
  , [ customAmounts ])

  const {
    investmentsToMove,
    investmentsToOverride,
    filesToMove,
    filesToOverride,
    transactionsToMove,
    transactionsToOverride,
  } = useTransferItems({
    table,
    targetProjectId,
    targetInvestments,
    sourceProjectId,
    sourceFiles,
    sourceInvestments,
    sourceTransactions,
    customTargets,
  })

  const {
    ensureTargetBatches,
  } = useTargetBatches({
    targetProjectId,
    filesToMove,
    filesToOverride,
    sourceBatches,
    targetBatches,
    setTargetBatches,
  })

  const onClick = async () => {
    setIsLoading(true)

    const { getTargetBatchId } = await ensureTargetBatches()

    const {
      transferInvestmentsPayload,
      transferTransactionsPayload,
      transferInvestorFilesPayload,
    } = getPayloads({
      sourceProjectId,
      targetProjectId,
      sourceInvestments,
      filesToMove,
      filesToOverride,
      transactionsToMove,
      transactionsToOverride,
      investmentsToMove,
      investmentsToOverride,
      getProjectName,
      customAmountsMap,
      getTargetBatchId,
    })

    const payloads = {
      TransferInvestments: transferInvestmentsPayload,
      TransferTransactions: transferTransactionsPayload,
      TransferInvestorFiles: transferInvestorFilesPayload,
    }

    // eslint-disable-next-line no-console
    console.info(JSON.stringify(payloads, null, 2))

    await request(transferInvestorFilesOperation, { mutation: transferInvestorFilesPayload })
    await request(transferTransactionsOperation, { mutation: transferTransactionsPayload })
    await request(transferInvestmentsOperation, { mutation: transferInvestmentsPayload })

    const transferredInvestments = [
      ...transferInvestmentsPayload.move,
      ...transferInvestmentsPayload.override,
    ]

    for (const transferredInvestment of transferredInvestments) {
      const { investmentId } = transferredInvestment

      const investment = [
        ...sourceInvestments,
        ...targetInvestments
      ].find(({ id }) => id === investmentId)

      const { note } = investment

      const usDate = getUsDateFromIsoDate(new Date())
      const sourceProjectName = getProjectName(sourceProjectId)
      const targetProjectName = getProjectName(targetProjectId)

      const updatedNote = [
        note,
        `Transferred from ${sourceProjectName} to ${targetProjectName} on ${usDate}`
      ].filter(value => !!value).join('\n')

      await request(updateInvestmentOperation, { id: investmentId, mutation: { note: updatedNote }})
    }

    await onAfterComplete()
  }

  const isDisabled =
    !targetProjectId ||
    !isTransferInvestmentsSubmitAvailable

  return (
    <Button
      type="primary"
      onClick={onClick}
      loading={isLoading}
      disabled={isDisabled}
    >
      {LABEL_SUBMIT}
    </Button>
  )
}

InvestmentsTransferFooter.propTypes = {
  table: PropTypes.shape().isRequired,
  targetBatches: PropTypes.arrayOf(IndexProjectBatchesOutputShape).isRequired,
  targetProjectId: PropTypes.string,
  targetInvestments: PropTypes.arrayOf(InvestmentShape).isRequired,
  sourceFiles: PropTypes.arrayOf(IndexBatchFilesOutputShape).isRequired,
  sourceBatches: PropTypes.arrayOf(IndexProjectBatchesOutputShape).isRequired,
  sourceProjectId: PropTypes.string.isRequired,
  sourceTransactions: PropTypes.arrayOf(IndexTransactionsOutputShape).isRequired,
  sourceInvestments: PropTypes.arrayOf(InvestmentShape).isRequired,
  customAmounts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  customTargets: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setTargetBatches: PropTypes.func.isRequired,
  onAfterComplete: PropTypes.func.isRequired,
}

export default InvestmentsTransferFooter;
